import {messages} from '@smartmixin/i18n';
import {ApiErrorResponse, HEADERS} from 'apisauce';
import {StatusCodes} from 'http-status-codes';
import {jwtDecode} from 'jwt-decode';
import moment from 'moment-timezone';
import React from 'react';
import {useIntl} from 'react-intl';
import {useDispatch, useStore} from 'react-redux';
import {useUserLanguage} from './userContext';
import {
  ErrorCode,
  SUPPORTED_API_VERSION,
  apiClient,
  getApiUrl,
  getDeviceId,
} from '../api';
import {ParsedApiError, parseClientError} from '../api/parseApiError';
import {
  REFRESHER_TIMEOUT_MS,
  TokenRefreshResult,
  TokenRefresher,
  getTokenRefresher,
} from '../api/tokenRefresher';
import {
  ApiRequestParams,
  ApiResponse,
  TypedApiRequestParams,
} from '../api/types';

type FieldInfo = ParsedApiError['fields'][string];
import {
  LOGIN_AS_TARGET_FORBIDDEN,
  STAFF_ELEVATION_REQUIRED,
  activeStaffElevationToken,
  clearStaffElevation,
  getDebugEmail,
  selectIsAdmin,
  selectStaffElevation,
  selectToken,
  setAlert,
  setDebugEmail,
  setUpgradeRequired,
  staffElevationRequired,
  storeToken,
} from '../store';
import {CommonRootState} from '../store/types';
import {JwtToken} from '../store/user/types';

export type ErrorInfo = {
  statusCode?: number;
  fields: Record<string, FieldInfo>;
  // Raw response body of CLIENT_ERROR responses, for callers that need
  // more than details/fields (e.g. the forecast-map build 409 payload
  // carrying a run object, or every message of a multi-error 400 field).
  data?: unknown;
  details?: {
    message: string;
    code: string | number;
  };
};

/**
 * Rejection reason for a debounced fetch superseded by a newer call to the
 * same endpoint before its timer fired. Superseded promises used to stay
 * pending forever — awaiters hung and `.finally` cleanup never ran. Callers
 * that debounce already ignore errors; distinguish with `instanceof` when a
 * superseded call must not be treated as a failure.
 */
export class DebounceSupersededError extends Error {
  constructor(url: string) {
    super(`Debounced request superseded: ${url}`);
    this.name = 'DebounceSupersededError';
    // Maintains proper stack trace for where our error was thrown (only in V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, DebounceSupersededError);
    }
  }
}

/**
 * Custom error class for API errors that extends Error.
 * This ensures proper error handling and Sentry reporting.
 */
export class ApiError extends Error {
  public readonly statusCode?: number;
  public readonly fields: Record<string, FieldInfo>;
  public readonly data?: unknown;
  public readonly details?: {
    message: string;
    code: string | number;
  };

  constructor(errorInfo: ErrorInfo) {
    super(errorInfo.details?.message || 'API Error');
    this.name = 'ApiError';
    this.statusCode = errorInfo.statusCode;
    this.fields = errorInfo.fields;
    this.data = errorInfo.data;
    this.details = errorInfo.details;
    // Maintains proper stack trace for where our error was thrown (only in V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiError);
    }
  }
}

type DecodedJwtToken = {
  token_type: string;
  exp: number;
  jti: string;
  user_id: number;
};

type RefreshTokenResponse = {
  refresh: string;
  access: string;
};

/** An access token with less validity left than this is refreshed. */
const ACCESS_TOKEN_MIN_VALIDITY_SECONDS = 5 * 60;

/**
 * The refresher's answer, or a transient one when it throws or takes longer
 * than `ms`: neither decides anything about the session, and a refresher that
 * never settles would otherwise pin every authenticated request.
 */
const settleWithin = (
  pending: Promise<TokenRefreshResult>,
  ms: number,
): Promise<TokenRefreshResult> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<TokenRefreshResult>((resolve) => {
    timer = setTimeout(
      () => resolve({status: 'transient', code: 'timeoutError'}),
      ms,
    );
  });
  return Promise.race([
    pending.catch(
      (): TokenRefreshResult => ({status: 'transient', code: 'unknownError'}),
    ),
    timeout,
  ]).finally(() => clearTimeout(timer));
};

const TRANSIENT_CODES = new Set<string>([
  'connectionError',
  'timeoutError',
  'serverError',
  'unknownError',
]);

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0;

/**
 * The result crosses a native bridge, so its shape is checked rather than
 * trusted: an unknown status, or a pair that is not two strings, decides
 * nothing (storing a malformed pair would break every later request).
 */
const checkedRefreshResult = (
  result: TokenRefreshResult,
): TokenRefreshResult => {
  switch (result?.status) {
    case 'ok':
      return isNonEmptyString(result.token?.accessToken) &&
        isNonEmptyString(result.token?.refreshToken)
        ? result
        : {status: 'transient', code: 'unknownError'};
    case 'signedOut':
    case 'expired':
    case 'rejected':
      return result;
    case 'transient':
      return TRANSIENT_CODES.has(result.code)
        ? result
        : {status: 'transient', code: 'unknownError'};
    default:
      return {status: 'transient', code: 'unknownError'};
  }
};

/** Whether two pairs were issued to the same user (the `user_id` claim). */
const sameUser = (a: JwtToken, b: JwtToken) => {
  try {
    const userA = (jwtDecode(a.accessToken) as DecodedJwtToken).user_id;
    const userB = (jwtDecode(b.accessToken) as DecodedJwtToken).user_id;
    return userA !== undefined && userA === userB;
  } catch {
    return false;
  }
};

/**
 * What a refresh may do with its outcome. A refresh can outlive the session
 * that started it (the user logs out, or out and back in, while it is in
 * flight). Its outcome then describes a session that no longer exists:
 * storing the minted pair would sign a logged-out user back in, and a late
 * rejection would log out the session that replaced it.
 */
type RefreshSession = {
  /** Whether the store still holds the session this refresh is for. */
  isCurrent: () => boolean;
  /** Get a token for whatever session the store holds now instead. */
  startOver: () => Promise<string>;
};

// Lazy initialization to allow API URL configuration before first use
let _client: ReturnType<typeof apiClient> | null = null;
const getClient = () => {
  if (!_client) {
    _client = apiClient(getApiUrl());
  }
  return _client;
};

/**
 * The refresh currently in flight, keyed by the refresh token it presents.
 * Deliberately module-level and not a ref: every component calling
 * useApiClient() gets its own hook instance, so a ref would only dedupe within
 * one component. A device runs exactly one token pair (a second device is a
 * separate process with its own state), so this single slot describes the whole
 * app's refresh at any instant.
 *
 * Without it, N parallel requests finding the access token inside its 5-minute
 * refresh window all POST `auth/token/refresh/` with the SAME refresh token.
 * Since backend !516 installed `rest_framework_simplejwt.token_blacklist` the
 * first of those consumes the token and the losers get a 401, which getToken
 * turns into a forced logout — the user is signed out for refreshing normally.
 */
let refreshInFlight: {refreshToken: string; promise: Promise<string>} | null =
  null;

/**
 * The pair minted by the last successful refresh, keyed by the refresh token it
 * consumed. `fetch` reads the stored token ONCE, at the moment it is called
 * (`selectToken(store.getState())`), and threads that snapshot through its
 * async continuation, so a request already in flight when a refresh lands
 * elsewhere still carries the CONSUMED refresh token by the time it reaches
 * `getToken` — past the in-flight window above, but just as blacklisted.
 * Substituting the minted pair for it closes that narrower window.
 */
let lastRefreshed: {consumed: string; pair: JwtToken} | null = null;

const forgetRefreshState = () => {
  refreshInFlight = null;
  lastRefreshed = null;
};

/**
 * The pair a caller holding `pair` continues on: the one the last refresh
 * minted, if `pair` is what it consumed. Compared through an explicit null
 * check, not `lastRefreshed?.`: a pair persisted without a refreshToken would
 * make `undefined === undefined` true and dereference a null slot.
 */
const continuing = (pair: JwtToken): JwtToken =>
  lastRefreshed && lastRefreshed.consumed === pair.refreshToken
    ? lastRefreshed.pair
    : pair;

/**
 * Reset the cached API client so the next call to getClient()
 * creates a fresh instance with the current API URL.
 * Must be called after configureApiUrl() to take effect.
 *
 * Also drops the single-flight refresh state: a different API URL is a
 * different backend, so any token minted by the previous one is meaningless.
 */
export const resetApiClient = () => {
  _client = null;
  forgetRefreshState();
};

const useApiClient = () => {
  const dispatch = useDispatch();
  // `store.getState()` at call time replaces the storedToken/debugEmail/
  // isAdmin refs below (react-hooks/refs, FE #180) — the same pattern
  // useUserData's editUserDetails uses for its own call-time read. `store`
  // is stable for the life of the Provider, so listing it in fetch's
  // dependency array below does not cost fetch its identity.
  const store = useStore<CommonRootState>();
  const language = useUserLanguage();
  const [loading, setLoading] = React.useState(false);
  const {formatMessage} = useIntl();

  // One debounce slot per endpoint: a newer call to the SAME url supersedes
  // the pending one (relied upon — e.g. useDashboardConfigSync's cumulative
  // diff), while calls to different urls no longer clobber each other (the
  // old single shared timer dropped whichever call was scheduled first).
  const debounceSlotsRef = React.useRef<
    Map<
      string,
      {timer: ReturnType<typeof setTimeout>; reject: (e: Error) => void}
    >
  >(new Map());
  // Number of unsettled fetches: `loading` must stay true until the LAST one
  // settles. A plain boolean went false as soon as any single fetch settled —
  // harmless before, but superseded debounced promises now settle (reject)
  // while their superseder is still pending.
  const pendingCountRef = React.useRef(0);

  // `language` and `formatMessage` stay mirrored into refs so `fetch` below
  // can read the CURRENT value at call time without listing either in its
  // dependency array (FE #180's deliberately-stable identity) — unlike
  // storedToken/debugEmail/isAdmin above, neither is reachable through
  // `store.getState()`:
  // - `language`: the underlying `selectUserLanguage` selector needs
  //   `deviceLanguage` as a second argument — an app-level constant threaded
  //   in as a prop from each app's own `./i18n` module at `UserProvider`,
  //   never written to Redux. Reconstructing it here would mean importing
  //   app-specific i18n setup into core, inverting the dependency direction
  //   the monorepo relies on (core has no app import).
  // - `formatMessage`: comes from react-intl's `useIntl()`, a React context
  //   value with no Redux-backed equivalent to read at call time.
  const languageRef = React.useRef(language);
  languageRef.current = language;
  const formatMessageRef = React.useRef(formatMessage);
  formatMessageRef.current = formatMessage;

  function debounce<T extends (...args: unknown[]) => unknown>(
    f: T,
    interval: number,
    key: string,
  ) {
    return (...args: Parameters<T>) => {
      const prior = debounceSlotsRef.current.get(key);
      if (prior) {
        clearTimeout(prior.timer);
        // Settle the superseded promise so awaiters don't hang forever and
        // `.finally` cleanup runs. NOTE: deliberately no unmount cleanup —
        // a timer surviving unmount still fires its request, which e.g.
        // saves a dashboard edit made right before navigating away.
        prior.reject(new DebounceSupersededError(key));
      }
      return new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
          debounceSlotsRef.current.delete(key);
          resolve(f(...args));
        }, interval);
        debounceSlotsRef.current.set(key, {timer, reject});
      });
    };
  }

  const fetch = React.useCallback(
    async <T = unknown,>(
      apiCall: TypedApiRequestParams<T> | ApiRequestParams,
      options: {
        debounce?: number;
        ignore404?: boolean;
        ignore401?: boolean;
        // Suppress the global error alert for 409 responses the caller
        // handles inline (e.g. forecast-map build conflicts).
        ignore409?: boolean;
        // Suppress the global error alert for ALL failures of this call —
        // the promise still rejects with the same ApiError. For polling
        // loops that retry transient failures and surface terminal
        // failure inline themselves (a long-running build poll must not
        // raise a snackbar on every network blip).
        silent?: boolean;
        skipLoginAs?: boolean;
        accessToken?: string;
        headers?: HEADERS;
      } = {},
    ): Promise<T> => {
      const fmtMsg = formatMessageRef.current;
      const errorHandler = (errorRes: ApiErrorResponse<unknown>) => {
        const info: ErrorInfo = {
          fields: {},
        };
        if (errorRes.problem === 'CLIENT_ERROR') {
          info.statusCode = errorRes.status;
          info.data = errorRes.data;
          if (errorRes.status === StatusCodes.BAD_REQUEST) {
            const parsed = parseClientError(errorRes.data);
            info.details = parsed.details;
            info.fields = parsed.fields;
            return info;
          }
          const detail = (errorRes.data as Record<string, unknown>)?.detail;
          // Plain-string DRF details (e.g. the forecast-map build 409
          // conflict) would crash the alert gate below reading `.code` —
          // normalize them into the structured shape.
          info.details =
            typeof detail === 'string'
              ? {message: detail, code: `http_${errorRes.status}`}
              : (detail as {message: string; code: string | number}); // global error
          info.fields = {};
        } else {
          if (errorRes.problem === 'CONNECTION_ERROR') {
            info.details = {
              message: fmtMsg(messages.network.connectionError),
              code: 'connectionError',
            };
          } else if (errorRes.problem === 'NETWORK_ERROR') {
            info.details = {
              message: fmtMsg(messages.network.networkError),
              code: 'networkError',
            };
          } else if (errorRes.problem === 'TIMEOUT_ERROR') {
            info.details = {
              message: fmtMsg(messages.network.timeoutError),
              code: 'timeoutError',
            };
          } else if (errorRes.problem === 'SERVER_ERROR') {
            if (errorRes.status === StatusCodes.SERVICE_UNAVAILABLE) {
              const detail = (errorRes.data as Record<string, unknown>)?.detail;
              // Same plain-string normalization as the CLIENT_ERROR path.
              info.details =
                typeof detail === 'string'
                  ? {message: detail, code: `http_${errorRes.status}`}
                  : (detail as {message: string; code: string | number});
            } else {
              info.details = {
                message: fmtMsg(messages.network.serverError),
                code: 'serverError',
              };
            }
          } else if (errorRes.problem === 'CANCEL_ERROR') {
            info.details = {
              message: fmtMsg(messages.network.cancelError),
              code: 'cancelError',
            };
          } else {
            info.details = {
              message: fmtMsg(messages.network.unknownError),
              code: 'unknownError',
            };
          }
          info.fields = {};
        }
        return info;
      };

      // Publish the pair a refresh produced in place of `consumed`.
      const adoptRefreshed = (consumed: string, pair: JwtToken) => {
        lastRefreshed = {consumed, pair};
        dispatch(storeToken(pair));
        return pair.accessToken;
      };

      // The network half of a refresh: presents this pair's refresh token once
      // and publishes what the backend mints back. Only ever entered through
      // getToken, which guarantees one caller per refresh token.
      const requestRefreshedToken = async (
        token: JwtToken,
        session: RefreshSession,
      ) => {
        getClient().deleteHeader('Authorization');
        const res = (await getClient().post('auth/token/refresh/', {
          refresh: token.refreshToken,
        })) as ApiResponse<RefreshTokenResponse, unknown>;
        if (!session.isCurrent()) {
          return session.startOver();
        }
        if (res.ok) {
          if (res.data) {
            const {refresh: refreshToken, access: accessToken} = res.data;
            return adoptRefreshed(token.refreshToken, {
              accessToken,
              refreshToken,
            });
          } else {
            // We should never be there
            return '';
          }
        } else {
          const err = errorHandler(res as ApiErrorResponse<unknown>);

          if (err.details) {
            dispatch(setAlert({message: err.details.message, type: 'ERROR'}));
          }

          if (err.statusCode === StatusCodes.UNAUTHORIZED) {
            // Drop the minted pair so it does not outlive the session in module
            // memory. Deliberately NOT the in-flight slot: clearing it here,
            // before this rejection has propagated, would let a caller arriving
            // in that window start a second doomed refresh instead of joining
            // this one. The `finally` below clears it a microtask later.
            lastRefreshed = null;
            dispatch({type: 'LOGOUT'});
          }
          return Promise.reject(new ApiError(err));
        }
      };

      // A refresh through the configured refresher (FE #218), which owns the
      // request. Core keeps the policy: what each outcome does to the session.
      // Same entry guarantee as requestRefreshedToken.
      const refreshThrough = async (
        refresher: TokenRefresher,
        token: JwtToken,
        session: RefreshSession,
      ): Promise<string> => {
        const result = checkedRefreshResult(
          await settleWithin(
            refresher({
              current: token,
              minValiditySeconds: ACCESS_TOKEN_MIN_VALIDITY_SECONDS,
              language: `${languageRef.current}`,
            }),
            REFRESHER_TIMEOUT_MS,
          ),
        );
        if (!session.isCurrent()) {
          return session.startOver();
        }
        switch (result.status) {
          case 'ok':
            return adoptRefreshed(token.refreshToken, result.token);
          case 'expired':
          case 'signedOut':
            // As for the 401 below, the in-flight slot is left to `finally`.
            lastRefreshed = null;
            dispatch({type: 'LOGOUT'});
            return '';
          case 'rejected': {
            if (result.details?.message) {
              dispatch(
                setAlert({message: result.details.message, type: 'ERROR'}),
              );
            }
            lastRefreshed = null;
            dispatch({type: 'LOGOUT'});
            return Promise.reject(
              new ApiError({
                statusCode: StatusCodes.UNAUTHORIZED,
                fields: {},
                details: result.details,
              }),
            );
          }
          case 'transient': {
            const details = {
              message: fmtMsg(messages.network[result.code]),
              code: result.code,
            };
            dispatch(setAlert({message: details.message, type: 'ERROR'}));
            return Promise.reject(new ApiError({fields: {}, details}));
          }
        }
      };

      const getToken = async (storedTokenPair: JwtToken | null) => {
        /* This is the automatic refresh token mechanism
        When getting the token, we ensure the stored access
        token is valid and if not, we refresh it using the
        stored refresh token */
        if (!storedTokenPair) {
          // There is not stored token
          return '';
        }

        // A caller holding the pair that the last refresh consumed continues on
        // the minted one instead of re-presenting a blacklisted token.
        const token = continuing(storedTokenPair);

        const decodedAccessToken = jwtDecode(
          token.accessToken,
        ) as DecodedJwtToken;
        if (
          moment().unix() <=
          moment.unix(decodedAccessToken.exp).unix() -
            ACCESS_TOKEN_MIN_VALIDITY_SECONDS
        ) {
          // stored accessToken is still valid, use it
          return token.accessToken;
        }

        const session: RefreshSession = {
          // The session is the pair the caller read from the store, or the one
          // a previous refresh minted from it. "Continuing" applies the same
          // substitution as above, so a pair that continues into `token` counts
          // as current: otherwise startOver could resolve back to `token` and
          // join this very flight, which would then wait on itself.
          isCurrent: () => {
            const held = selectToken(store.getState());
            if (!held?.refreshToken) {
              return false;
            }
            return (
              held.refreshToken === storedTokenPair.refreshToken ||
              continuing(held).refreshToken === token.refreshToken
            );
          },
          // Only for the same user: a request issued by one account must not
          // go out with the credentials of the account that replaced it.
          startOver: () => {
            const held = selectToken(store.getState());
            if (!held) {
              lastRefreshed = null;
            }
            // A pair without a refresh token is never current: going round
            // again would recurse forever.
            return held?.refreshToken && sameUser(held, token)
              ? getToken(held)
              : Promise.resolve('');
          },
        };
        // A stale snapshot (a debounced call, say) must not present a token the
        // store no longer holds: after a logout that would POST a dead session,
        // and its expired refresh token would log out the session after it.
        if (!session.isCurrent()) {
          return session.startOver();
        }

        // Refresh Token in order to get a renewed token pair (access + refresh)
        if (
          refreshInFlight &&
          refreshInFlight.refreshToken === token.refreshToken
        ) {
          // Someone is already presenting this exact token: await their result
          // rather than racing them into the backend's blacklist.
          return refreshInFlight.promise;
        }

        const refresher = getTokenRefresher();
        // A configured refresher judges the refresh token's expiry itself,
        // against its own token of record: a widget may have rotated that past
        // the pair this store holds.
        if (!refresher) {
          const decodedRefreshToken = jwtDecode(
            token.refreshToken,
          ) as DecodedJwtToken;
          if (moment().unix() > moment.unix(decodedRefreshToken.exp).unix()) {
            forgetRefreshState();
            dispatch({type: 'LOGOUT'});
            return '';
          }
        }

        const flight = (
          refresher
            ? refreshThrough(refresher, token, session)
            : requestRefreshedToken(token, session)
        ).finally(() => {
          // Identity-checked: a later flight (a re-login, say) must not have its
          // slot cleared by this one settling.
          if (refreshInFlight?.promise === flight) {
            refreshInFlight = null;
          }
        });
        refreshInFlight = {refreshToken: token.refreshToken, promise: flight};
        return flight;
      };

      const fetchData = async (
        requestParams: ApiRequestParams,
        userToken: JwtToken | null,
      ) => {
        const client = getClient();
        client.setHeader('X-SmartMixin-Context', 'UI');
        client.setHeader('Accept-Language', `${languageRef.current}`);
        // Anonymous mobile premium: the backend recognizes pre-account store
        // purchases through the device id (see configureDeviceId).
        const deviceId = getDeviceId();
        if (deviceId) {
          client.setHeader('X-Device-Id', deviceId);
        }
        const {url, method, params} = requestParams;

        const token =
          options && options.accessToken
            ? options.accessToken
            : await getToken(userToken);
        // Do not use Authorization header if token is empty
        if (!token) {
          client.deleteHeader('Authorization');
        } else {
          // it's an SmartMixin API call
          client.setHeader('Authorization', `Bearer ${token}`);
        }

        // Read fresh from the store at call time (FE #180) rather than a
        // ref mirrored on the last render — the debug-as-admin header must
        // reflect who is CURRENTLY debugging, not who was at mount/last
        // re-render.
        const currentState = store.getState();
        const debugEmail = getDebugEmail(currentState);
        const isAdmin = selectIsAdmin(currentState);
        // The impersonated email THIS request carries, if any -- kept so the
        // 403 handling below can tell its own answer from a stale one.
        const loginAsEmail =
          debugEmail && isAdmin && !options?.skipLoginAs ? debugEmail : null;
        if (loginAsEmail) {
          client.setHeader('X-Login-As', `${loginAsEmail}`);
        } else {
          client.deleteHeader('X-Login-As');
        }

        // Staff second factor. Sent on EVERY admin request while an unexpired
        // token is held -- skipLoginAs included, since user/search and
        // user/presets are exactly the skipLoginAs calls the backend gates.
        // Deleted otherwise: setHeader is sticky on this shared client, so a
        // previous request's token would otherwise ride along.
        const staffElevation = selectStaffElevation(currentState);
        const elevationToken = isAdmin
          ? activeStaffElevationToken(
              staffElevation,
              Math.floor(Date.now() / 1000),
            )
          : null;
        if (elevationToken) {
          client.setHeader('X-Staff-Elevation', elevationToken);
        } else {
          client.deleteHeader('X-Staff-Elevation');
          if (isAdmin && staffElevation.token !== null) {
            // Held but expired: forget it rather than keep a dead credential.
            dispatch(clearStaffElevation());
          }
        }

        if (options && options.headers) {
          Object.entries(options.headers).forEach(([key, value]) => {
            client.setHeader(key, value);
          });
        }

        let res;
        if (method === 'GET') {
          res = await client.get(url, params);
        } else if (method === 'POST') {
          res = await client.post(url, params);
        } else if (method === 'PATCH') {
          res = await client.patch(url, params);
        } else if (method === 'PUT') {
          res = await client.put(url, params);
        } else if (method === 'DELETE') {
          res = await client.delete(url, params);
        } else {
          throw new Error(`HTTP method not allowed: ${method}`);
        }

        if (res.headers && Object.keys(res.headers).includes('x-api-version')) {
          if (
            SUPPORTED_API_VERSION < parseInt(res.headers['x-api-version'], 10)
          ) {
            dispatch(setUpgradeRequired(true));
          }
        }

        if (!res.ok) {
          const err = errorHandler(res as ApiErrorResponse<unknown>);

          const forbiddenCode =
            err.statusCode === StatusCodes.FORBIDDEN
              ? err.details?.code
              : undefined;
          if (
            forbiddenCode === STAFF_ELEVATION_REQUIRED ||
            forbiddenCode === LOGIN_AS_TARGET_FORBIDDEN
          ) {
            // Judged against the state NOW, after the response: only a
            // request that still describes the current session may end it.
            // Of a burst of refused requests, the first one ends Login As and
            // the rest find it already ended -- one message, not a snackbar
            // per request.
            const now = store.getState();
            const stillImpersonating =
              loginAsEmail !== null && getDebugEmail(now) === loginAsEmail;

            if (forbiddenCode === STAFF_ELEVATION_REQUIRED) {
              // A token obtained while this request was in flight is not
              // what the backend refused: leave it, and the session, alone.
              const stale = selectStaffElevation(now).token !== elevationToken;
              // Raises the Debug screens' code prompt, `silent` or not. Never
              // retried here: the admin re-runs the action once elevated.
              dispatch(staffElevationRequired({rejectedToken: elevationToken}));
              if (stillImpersonating && !stale) {
                // debugEmail is persisted and the token is not: after a
                // restart or the 12 h expiry EVERY Login As request lands
                // here. End the impersonation and say where to go.
                dispatch(setDebugEmail(''));
                dispatch(
                  setAlert({
                    message: fmtMsg(
                      messages.auth.staffVerificationLoginAsStopped,
                    ),
                    type: 'WARNING',
                  }),
                );
              }
            } else if (stillImpersonating) {
              // A staff account cannot be impersonated, with or without a
              // code: leaving it as the target would refuse every request.
              dispatch(setDebugEmail(''));
              if (!options?.silent && err.details) {
                dispatch(
                  setAlert({message: err.details.message, type: 'ERROR'}),
                );
              }
            }
            // Either way the snackbar (if any) was decided above.
            return Promise.reject(new ApiError(err));
          }

          if (
            !(options && options.silent) &&
            err.details &&
            !ErrorCode.HANDLED_ERROR_CODES.includes(err.details.code.toString())
          ) {
            if (err.statusCode === StatusCodes.NOT_FOUND) {
              if (!(options && options.ignore404)) {
                dispatch(
                  setAlert({message: err.details.message, type: 'ERROR'}),
                );
              }
            } else if (err.statusCode === StatusCodes.UNAUTHORIZED) {
              if (!(options && options.ignore401)) {
                dispatch(
                  setAlert({message: err.details.message, type: 'ERROR'}),
                );
              }
            } else if (err.statusCode === StatusCodes.CONFLICT) {
              if (!(options && options.ignore409)) {
                dispatch(
                  setAlert({message: err.details.message, type: 'ERROR'}),
                );
              }
            } else {
              dispatch(setAlert({message: err.details.message, type: 'ERROR'}));
            }
          }

          return Promise.reject(new ApiError(err));
        } else {
          return res.data as T;
        }
      };

      const methodCall =
        options && options.debounce
          ? debounce(fetchData, options.debounce, apiCall.url)
          : fetchData;

      pendingCountRef.current += 1;
      setLoading(true);

      // Clear loading once the request SETTLES, whichever way it settles.
      // This used to sit on the straight-line path inside fetchData, after the
      // await but before the !res.ok branch, so it only ran when the request
      // returned a response. If the request *threw* — network drop, DNS
      // failure, timeout, an unhandled method — loading stayed true forever.
      // Any screen rendering an overlaid spinner off `loading` then swallowed
      // every touch, which is how the login dialog became impossible to close
      // after a failed Facebook sign-in.
      // Counted, not boolean: a superseded debounced call settles (rejects)
      // while its superseder is still in flight — loading must survive that.
      return (
        methodCall(apiCall, selectToken(store.getState())) as Promise<T>
      ).finally(() => {
        pendingCountRef.current -= 1;
        if (pendingCountRef.current === 0) {
          setLoading(false);
        }
      });
    },
    [dispatch, store],
  );
  return {fetch, loading};
};

export default useApiClient;
